import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface LeaderboardBowlerDetailProps {
  bowlerId: number;
  bowlerName: string;
  teamName: string;
  leagueId: number;
  onClose: () => void;
}

interface GameHistoryEntry {
  game_id: number;
  game_number: number;
  squad_name: string;
  squad_date: string;
  score: number;
  score_with_handicap: number;
  series_total: number;
  series_with_handicap: number;
  average_for_series: number;
}

interface BowlerStats {
  total_games: number;
  overall_average: number;
  high_game: number;
  series_average: number;
  high_series: number;
}

interface BowlerDetailData {
  bowler: {
    id: number;
    bowler_name: string;
    team_id: number;
    team_name: string;
  };
  gameHistory: GameHistoryEntry[];
  overallStats: BowlerStats;
}

const LeaderboardBowlerDetail: React.FC<LeaderboardBowlerDetailProps> = ({ 
  bowlerId, 
  bowlerName, 
  teamName, 
  leagueId, 
  onClose 
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<BowlerDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBowlerDetails = async () => {
      try {
        setLoading(true);
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        
        const response = await fetch(
          `${apiUrl}/admin/leaderboard/leagues/${leagueId}/bowler/${bowlerId}/details`,
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }
        );

        if (response.ok) {
          const result = await response.json();
          setData(result);
        } else {
          setError(t('admin.leaderboardBowlerDetail.error'));
        }
      } catch (error) {
        console.error('Error fetching bowler details:', error);
        setError(t('admin.leaderboardBowlerDetail.errorOccurred'));
      } finally {
        setLoading(false);
      }
    };

    fetchBowlerDetails();
  }, [bowlerId, leagueId, t]);

  // Group game history by match (series)
  const groupedHistory = data?.gameHistory.reduce((acc: any, game) => {
    const key = `${game.squad_name}-${game.squad_date}`;
    if (!acc[key]) {
      acc[key] = {
        squad_name: game.squad_name,
        squad_date: game.squad_date,
        series_total: game.series_total,
        series_with_handicap: game.series_with_handicap,
        average_for_series: game.average_for_series,
        games: []
      };
    }
    // Update series totals if current values are null but game has values
    if (!acc[key].series_total && game.series_total) {
      acc[key].series_total = game.series_total;
    }
    if (!acc[key].series_with_handicap && game.series_with_handicap) {
      acc[key].series_with_handicap = game.series_with_handicap;
    }
    if (!acc[key].average_for_series && game.average_for_series) {
      acc[key].average_for_series = game.average_for_series;
    }
    if (game.score !== null) {
      acc[key].games.push({
        game_number: game.game_number,
        score: game.score,
        score_with_handicap: game.score_with_handicap
      });
    }
    return acc;
  }, {}) || {};

  const seriesHistory = Object.values(groupedHistory).sort((a: any, b: any) => 
    new Date(b.squad_date).getTime() - new Date(a.squad_date).getTime()
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-white/20">
          <div>
            <h2 className="text-2xl font-bold text-white">🎳 {bowlerName}</h2>
            <p className="text-blue-200">{t('admin.leaderboardBowlerDetail.team')} {teamName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-red-300 transition duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center text-white py-8">{t('admin.leaderboardBowlerDetail.loading')}</div>
          ) : error ? (
            <div className="text-center text-red-300 py-8">{error}</div>
          ) : data ? (
            <>
              {/* Overall Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                <div className="bg-blue-600/20 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{data.overallStats.total_games || 0}</div>
                  <div className="text-blue-200 text-sm">{t('admin.leaderboardBowlerDetail.stats.totalGames')}</div>
                </div>
                <div className="bg-green-600/20 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{data.overallStats.overall_average || 0}</div>
                  <div className="text-green-200 text-sm">{t('admin.leaderboardBowlerDetail.stats.average')}</div>
                </div>
                <div className="bg-yellow-600/20 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{data.overallStats.high_game || 0}</div>
                  <div className="text-yellow-200 text-sm">{t('admin.leaderboardBowlerDetail.stats.highGame')}</div>
                </div>
                <div className="bg-purple-600/20 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{data.overallStats.series_average || 0}</div>
                  <div className="text-purple-200 text-sm">{t('admin.leaderboardBowlerDetail.stats.seriesAvg')}</div>
                </div>
                <div className="bg-red-600/20 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{data.overallStats.high_series || 0}</div>
                  <div className="text-red-200 text-sm">{t('admin.leaderboardBowlerDetail.stats.highSeries')}</div>
                </div>
              </div>

              {/* Game History */}
              <div>
                <h3 className="text-xl font-bold text-white mb-4">{t('admin.leaderboardBowlerDetail.gameHistory')}</h3>
                {seriesHistory.length === 0 ? (
                  <div className="text-center text-blue-200 py-8">{t('admin.leaderboardBowlerDetail.noHistory')}</div>
                ) : (
                  <div className="space-y-4">
                    {seriesHistory.map((series: any, index) => (
                      <div key={index} className="bg-white/10 rounded-lg p-4 border border-white/20">
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <div className="text-lg font-bold text-white">{series.squad_name}</div>
                            <div className="text-blue-200 text-sm">
                              {new Date(series.squad_date).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-green-300">
                              {t('admin.leaderboardBowlerDetail.series')} {series.series_total || 0}
                            </div>
                            <div className="text-sm text-green-200">
                              {t('admin.leaderboardBowlerDetail.withHandicap')} {series.series_with_handicap || 0}
                            </div>
                          </div>
                        </div>
                        
                        {/* Individual Games */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {series.games.map((game: any, gameIndex: number) => (
                            <div key={gameIndex} className="bg-white/5 rounded p-3 text-center">
                              <div className="text-sm text-blue-200 mb-1">{t('admin.leaderboardBowlerDetail.game', { number: game.game_number })}</div>
                              <div className="text-lg font-bold text-white">{game.score}</div>
                              <div className="text-sm text-gray-300">{t('admin.leaderboardBowlerDetail.withHandicap')} {game.score_with_handicap}</div>
                            </div>
                          ))}
                        </div>
                        
                        {series.average_for_series && (
                          <div className="mt-3 text-center">
                            <span className="text-sm text-purple-200">
                              {t('admin.leaderboardBowlerDetail.seriesAverage', { average: series.average_for_series })}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center text-blue-200 py-8">{t('admin.leaderboardBowlerDetail.noData')}</div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/20 flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200"
          >
            {t('admin.leaderboardBowlerDetail.backButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardBowlerDetail;